// Toast notifications, top-right anchored, with a draining progress bar that
// indicates remaining TTL. Each toast slides in from the right and fades+slides
// out when it's dismissed (manual click or auto-expire).
//
// API surface stays the same as before: useToast().success/error/info(title, body?).

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { CheckCircle2, AlertCircle, Info, X, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/cn'

type Tone = 'success' | 'error' | 'info'

interface Toast {
  id: string
  tone: Tone
  title: string
  body?: string
  ttl: number              // Total lifetime (ms). Drives the progress bar.
  bornAt: number           // performance.now() at insert; used for resume after pause
  exiting?: boolean        // true once dismissed but still animating out
}

interface ToastApi {
  show: (t: Omit<Toast, 'id' | 'ttl' | 'bornAt'> & { ttl?: number }) => void
  success: (title: string, body?: string) => void
  error: (title: string, body?: string) => void
  info: (title: string, body?: string) => void
}

const Ctx = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}

const ICON: Record<Tone, LucideIcon> = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
}

// Tone-specific palette: outer border + body bg + accent bar color.
const TONE_STYLE: Record<Tone, string> = {
  success: 'border-success/30 bg-success/10',
  error:   'border-danger/30 bg-danger/10',
  info:    'border-primary/30 bg-primary/10',
}
const TONE_TEXT: Record<Tone, string> = {
  success: 'text-success',
  error:   'text-danger',
  info:    'text-primary',
}
const TONE_BAR: Record<Tone, string> = {
  success: 'bg-success',
  error:   'bg-danger',
  info:    'bg-primary',
}

const DEFAULT_TTL = 3500     // ms
const EXIT_DURATION = 220    // ms — must match CSS transition

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const idRef = useRef(0)

  // Mark a toast as exiting (triggers slide-out CSS), then drop it after
  // EXIT_DURATION so the unmount happens cleanly.
  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)))
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), EXIT_DURATION)
  }, [])

  const show = useCallback<ToastApi['show']>((t) => {
    const id = String(++idRef.current)
    const ttl = t.ttl ?? DEFAULT_TTL
    const next: Toast = { id, ttl, bornAt: performance.now(), tone: t.tone, title: t.title, body: t.body }
    setToasts((prev) => [...prev, next])
    // Auto-dismiss after ttl. We schedule a dismiss (which then takes
    // EXIT_DURATION to actually unmount) so the UI is gone by ttl + EXIT_DURATION.
    setTimeout(() => dismiss(id), ttl)
  }, [dismiss])

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (title, body) => show({ tone: 'success', title, body }),
    error:   (title, body) => show({ tone: 'error',   title, body }),
    info:    (title, body) => show({ tone: 'info',    title, body }),
  }), [show])

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed top-4 right-4 z-[60] flex flex-col gap-2 max-w-sm w-[360px]">
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)}/>
        ))}
      </div>
    </Ctx.Provider>
  )
}

// Each toast is its own component so the progress bar can drive itself off
// the toast's bornAt + ttl without re-rendering all sibling toasts.
function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const Icon = ICON[toast.tone]
  return (
    <div
      role="status"
      data-exiting={toast.exiting ? '' : undefined}
      className={cn(
        'pointer-events-auto relative overflow-hidden rounded-lg border shadow-lg backdrop-blur',
        'bg-card/95',                       // solid card surface so text is readable on busy pages
        TONE_STYLE[toast.tone],
        // Slide-in from the right + fade. CSS keyframe defined in index.css.
        'animate-[toastIn_0.22s_cubic-bezier(0.2,0.8,0.2,1)]',
        // When exiting, slide back out + fade. We use a transition class so the
        // change happens on the data-attribute toggle, not on a re-mount.
        'transition-all duration-[220ms] ease-out',
        'data-[exiting]:translate-x-2 data-[exiting]:opacity-0',
      )}
    >
      <div className="px-3.5 py-3 flex items-start gap-2.5">
        <Icon size={16} strokeWidth={2} className={cn('shrink-0 mt-0.5', TONE_TEXT[toast.tone])}/>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium leading-tight text-foreground">{toast.title}</div>
          {toast.body && (
            <div className="text-xs text-muted-foreground mt-1 leading-snug">{toast.body}</div>
          )}
        </div>
        <button
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground transition-colors shrink-0 -mr-1 -mt-0.5"
          aria-label="Dismiss"
        >
          <X size={14}/>
        </button>
      </div>

      {/* Progress bar — drains from 100% → 0% over `ttl`. We start the
          animation at the toast's birth time, so even if the component is
          re-rendered later it stays in sync. */}
      <div
        className={cn('absolute bottom-0 left-0 h-0.5 origin-left', TONE_BAR[toast.tone])}
        style={{
          width: '100%',
          animation: `toastDrain ${toast.ttl}ms linear forwards`,
        }}
      />
    </div>
  )
}
